import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Gift, ExternalLink, ShoppingCart, Check, Plus, Trash2, Star, Heart, Package } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CATEGORIES = ['All', 'Fashion', 'Art Supplies', 'Tech', 'Home', 'Experiences'];

export default function WishlistPage() {
  const { user, token } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [activeCategory, setActiveCategory] = useState('All');
  const [title, setTitle] = useState('');
  const [url, setUrl] = useState('');
  const [imageUrl, setImageUrl] = useState('');
  const [price, setPrice] = useState('');
  const [priority, setPriority] = useState('medium');
  const [itemCategory, setItemCategory] = useState('Tech');

  const fetchItems = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/wishlist`);
      setItems(res.data.items);
    } catch (err) { console.error(err); }
    setLoading(false);
  }, []);

  useEffect(() => { fetchItems(); }, [fetchItems]);

  const handleAdd = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/wishlist`, { title, url, image_url: imageUrl, price, priority },
        { headers: { Authorization: `Bearer ${token}` } });
      setTitle(''); setUrl(''); setImageUrl(''); setPrice(''); setShowForm(false);
      fetchItems();
    } catch (err) { console.error(err); }
  };

  const handlePurchase = async (itemId) => {
    if (!token) return;
    try {
      await axios.put(`${API}/wishlist/${itemId}/purchase`, {}, { headers: { Authorization: `Bearer ${token}` } });
      fetchItems();
    } catch (err) { console.error(err); }
  };

  const handleDelete = async (itemId) => {
    try {
      await axios.delete(`${API}/wishlist/${itemId}`, { headers: { Authorization: `Bearer ${token}` } });
      fetchItems();
    } catch (err) { console.error(err); }
  };

  const isCreator = user?.role === 'creator' || user?.role === 'admin';
  const giftedCount = items.filter(i => i.purchased).length;
  const totalCount = items.length;

  // Activity feed (mock based on purchased items)
  const recentGifts = items.filter(i => i.purchased).slice(0, 5);

  return (
    <div className="min-h-screen bg-texture py-8 px-4" data-testid="wishlist-page">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-6">
          <div>
            <h1 className="text-3xl font-black flex items-center gap-3" data-testid="wishlist-title">
              <Heart className="text-[--ch-magenta]" size={28} />
              <span>My <span className="neon-magenta">Wishlist</span></span>
            </h1>
            <div className="flex items-center gap-3 mt-2">
              <span className="text-sm font-light text-[--ch-text-secondary]">
                {giftedCount} of {totalCount} items gifted
              </span>
              {/* Progress bar */}
              <div className="w-32 h-1.5 bg-[--ch-border] rounded-full overflow-hidden">
                <div className="h-full rounded-full bg-gradient-to-r from-[--ch-cyan] to-[--ch-magenta]"
                  style={{ width: totalCount > 0 ? `${(giftedCount / totalCount) * 100}%` : '0%' }} />
              </div>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-[rgba(245,158,11,0.1)] border border-[rgba(245,158,11,0.2)]">
              <Package size={14} className="text-[--ch-warning]" />
              <span className="text-[10px] font-bold text-[--ch-warning]">Powered by Amazon Wishlist</span>
            </div>
            {isCreator && (
              <button data-testid="add-wishlist-btn" onClick={() => setShowForm(!showForm)}
                className="btn-neon px-4 py-2 rounded-lg text-xs flex items-center gap-1">
                <Plus size={14} /> Add Item
              </button>
            )}
          </div>
        </div>

        {/* Category Filters */}
        <div className="flex gap-2 mb-6 overflow-x-auto pb-2" data-testid="wishlist-categories">
          {CATEGORIES.map(cat => (
            <button key={cat} onClick={() => setActiveCategory(cat)}
              data-testid={`wishcat-${cat.toLowerCase().replace(' ', '-')}`}
              className={`px-4 py-2 rounded-full text-xs font-semibold border whitespace-nowrap transition-all ${
                activeCategory === cat
                  ? 'border-[--ch-cyan] text-[--ch-cyan] bg-[rgba(0,240,255,0.1)]'
                  : 'border-[--ch-border] text-[--ch-text-secondary] hover:text-white'
              }`}>
              {cat}
            </button>
          ))}
        </div>

        {/* Add form */}
        {showForm && isCreator && (
          <div className="ch-card p-6 mb-6 animate-fade-in-up" data-testid="wishlist-form">
            <h3 className="text-sm font-semibold mb-4">Add Wishlist Item</h3>
            <form onSubmit={handleAdd} className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <input data-testid="wishlist-title-input" value={title} onChange={e => setTitle(e.target.value)}
                className="w-full bg-[--ch-bg-secondary] border border-[--ch-border] rounded-lg px-4 py-2.5 text-white focus:border-[--ch-cyan] focus:outline-none text-sm"
                placeholder="Item name" required />
              <input data-testid="wishlist-url-input" value={url} onChange={e => setUrl(e.target.value)}
                className="w-full bg-[--ch-bg-secondary] border border-[--ch-border] rounded-lg px-4 py-2.5 text-white focus:border-[--ch-cyan] focus:outline-none text-sm"
                placeholder="Amazon URL" required />
              <input data-testid="wishlist-image-input" value={imageUrl} onChange={e => setImageUrl(e.target.value)}
                className="w-full bg-[--ch-bg-secondary] border border-[--ch-border] rounded-lg px-4 py-2.5 text-white focus:border-[--ch-cyan] focus:outline-none text-sm"
                placeholder="Image URL" />
              <input data-testid="wishlist-price-input" value={price} onChange={e => setPrice(e.target.value)}
                className="w-full bg-[--ch-bg-secondary] border border-[--ch-border] rounded-lg px-4 py-2.5 text-white focus:border-[--ch-cyan] focus:outline-none text-sm"
                placeholder="Price (e.g. $49.99)" />
              <div className="sm:col-span-2 flex items-center gap-3">
                <div className="flex gap-1">
                  {['low', 'medium', 'high'].map(p => (
                    <button key={p} type="button" data-testid={`priority-${p}`} onClick={() => setPriority(p)}
                      className={`px-3 py-1.5 rounded-lg text-[10px] font-bold border transition-all ${
                        priority === p ? 'border-[--ch-magenta] text-[--ch-magenta] bg-[rgba(255,0,229,0.1)]' : 'border-[--ch-border] text-[--ch-text-secondary]'}`}>
                      {p.charAt(0).toUpperCase() + p.slice(1)}
                    </button>
                  ))}
                </div>
                <button data-testid="wishlist-submit-btn" type="submit" className="btn-neon px-6 py-2 rounded-lg text-xs ml-auto">
                  Add to Wishlist
                </button>
              </div>
            </form>
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
          {/* Items Grid */}
          <div className="lg:col-span-3">
            {loading ? (
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
                {[1,2,3,4,5,6].map(i => <div key={i} className="ch-card aspect-square animate-pulse bg-[--ch-bg-secondary]" />)}
              </div>
            ) : items.length === 0 ? (
              <div className="text-center py-20" data-testid="empty-wishlist">
                <Gift size={48} className="mx-auto text-[--ch-text-secondary] mb-4 opacity-30" />
                <p className="font-medium">No wishlist items yet.</p>
                <p className="text-sm font-light text-[--ch-text-secondary] mt-1">Check back soon!</p>
              </div>
            ) : (
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-4" data-testid="wishlist-grid">
                {items.map((item, i) => (
                  <div key={item.id} className={`ch-card overflow-hidden animate-fade-in-up group ${item.purchased ? 'opacity-60' : ''}`}
                    style={{ animationDelay: `${i * 0.05}s`, borderBottom: item.purchased ? '2px solid var(--ch-success)' : '2px solid transparent', backgroundImage: 'linear-gradient(var(--ch-bg-card), var(--ch-bg-card)), linear-gradient(135deg, var(--ch-cyan), var(--ch-magenta))', backgroundOrigin: 'border-box', backgroundClip: 'padding-box, border-box' }}
                    data-testid={`wishlist-item-${item.id}`}>
                    {/* Image */}
                    <div className="aspect-square bg-[--ch-bg-secondary] overflow-hidden relative">
                      {item.image_url ? (
                        <img src={item.image_url} alt={item.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center">
                          <Gift size={32} className="text-[--ch-text-secondary] opacity-20" />
                        </div>
                      )}
                      {item.purchased && (
                        <div className="absolute top-2 right-2 bg-[--ch-success] text-black text-[10px] font-bold px-2 py-0.5 rounded-full flex items-center gap-1">
                          <Check size={10} /> Purchased
                        </div>
                      )}
                      {item.priority === 'high' && !item.purchased && (
                        <div className="absolute top-2 left-2">
                          <Star size={16} className="text-[--ch-warning] fill-[--ch-warning]" />
                        </div>
                      )}
                    </div>
                    {/* Info */}
                    <div className="p-3">
                      <h3 className="text-sm font-semibold truncate">{item.title}</h3>
                      {item.price && (
                        <p className="text-lg font-bold neon-cyan mt-0.5">{item.price}</p>
                      )}
                      <div className="flex items-center gap-2 mt-2">
                        {item.purchased ? (
                          <span className="text-[10px] font-light text-[--ch-success]">
                            Gifted by @{item.purchased_by}
                          </span>
                        ) : (
                          <>
                            <a href={item.url} target="_blank" rel="noopener noreferrer"
                              data-testid={`buy-btn-${item.id}`}
                              className="btn-neon px-3 py-1.5 rounded-lg text-[10px] flex items-center gap-1">
                              <ShoppingCart size={10} /> Gift This Item
                            </a>
                            {token && (
                              <button data-testid={`mark-purchased-${item.id}`}
                                onClick={() => handlePurchase(item.id)}
                                className="text-[10px] font-light text-[--ch-text-secondary] hover:text-[--ch-cyan] transition-colors">
                                Mark Bought
                              </button>
                            )}
                          </>
                        )}
                        {isCreator && (
                          <button data-testid={`delete-wish-${item.id}`}
                            onClick={() => handleDelete(item.id)}
                            className="ml-auto text-[--ch-text-secondary] hover:text-[--ch-error] transition-colors">
                            <Trash2 size={12} />
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Recent Gift Activity Sidebar */}
          <div className="lg:col-span-1" data-testid="gift-activity">
            <div className="ch-card p-4 sticky top-20">
              <h3 className="text-sm font-semibold flex items-center gap-2 mb-4">
                <Gift size={14} className="text-[--ch-magenta]" /> Recent Gift Activity
              </h3>
              {recentGifts.length === 0 ? (
                <p className="text-xs font-light text-[--ch-text-secondary]">No gifts yet. Be the first!</p>
              ) : (
                <div className="space-y-3">
                  {recentGifts.map(gift => (
                    <div key={gift.id} className="flex items-start gap-2 pb-3 border-b border-[--ch-border] last:border-0">
                      <div className="w-6 h-6 rounded-full bg-[--ch-bg-secondary] flex items-center justify-center flex-shrink-0 text-[10px] font-bold text-[--ch-magenta]">
                        {gift.purchased_by?.[0]?.toUpperCase() || '?'}
                      </div>
                      <div>
                        <p className="text-xs">
                          <span className="font-semibold text-[--ch-cyan]">@{gift.purchased_by}</span>
                          <span className="font-light text-[--ch-text-secondary]"> gifted </span>
                          <span className="font-medium">{gift.title}</span>
                        </p>
                        <p className="text-[10px] font-light text-[--ch-text-secondary] mt-0.5">Thank you so much!</p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
              <div className="mt-4 p-3 rounded-lg bg-[rgba(255,0,229,0.05)] border border-[rgba(255,0,229,0.15)]">
                <p className="text-[10px] text-center">
                  <span className="font-semibold text-[--ch-magenta]">Want to make their day?</span><br />
                  <span className="font-light text-[--ch-text-secondary]">Gift an item and get a personal thank you!</span>
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
